/**
 * Base API Client using native fetch
 * Shared between all resource clients
 */

import {
    TagadaApiError,
    TagadaAuthError,
    TagadaNetworkError,
    TagadaCircuitBreakerError,
    TagadaError,
    TagadaErrorCode,
} from '../errors';
import { SDK_NAME, SDK_VERSION } from '../../../version';

export interface RequestConfig {
    headers?: Record<string, string>;
    skipAuth?: boolean;
    params?: Record<string, string>;
    signal?: AbortSignal;
    /** False: a refused session token is thrown to the caller, never renewed and retried. Default true. */
    renewRejectedToken?: boolean;
}

/** Answers a token to replace the one the server refused, or null when none can be had. */
export type RejectedTokenHandler = (rejectedToken: string) => Promise<string | null>;

export interface ApiClientConfig {
    baseURL: string;
    headers?: Record<string, string>;
    timeout?: number;
    /** Called when the server refuses the token a request carried; the request is sent once more with its answer. */
    onRejectedToken?: RejectedTokenHandler;
}

export type TokenProvider = () => Promise<string | null>;

// The CMS routes answer 401 with this text when the session behind a token is gone or expired. A 401 with any other
// text (a route that needs a logged-in buyer, another store) is not cured by a new anonymous token.
const REJECTED_SESSION_MESSAGE = 'Invalid or expired session';

/** The server refused the token itself: its session no longer exists or has expired. */
export function isRejectedSession(error: unknown): boolean {
    return (
        error instanceof TagadaAuthError &&
        error.statusCode === 401 &&
        error.message.startsWith(REJECTED_SESSION_MESSAGE)
    );
}

export class ApiClient {
    private baseURL: string;
    private timeout: number;
    private defaultHeaders: Record<string, string>;
    private currentToken: string | null = null;
    private tokenProvider: TokenProvider | null = null;
    private rejectedTokenHandler: RejectedTokenHandler | null = null;

    // Circuit breaker state
    private requestHistory: Map<string, { count: number; firstRequestTime: number }> = new Map();
    private readonly WINDOW_MS = 5000; // 5 seconds window
    private readonly MAX_REQUESTS = 30; // Max 30 requests per endpoint in window

    constructor(config: ApiClientConfig) {
        this.baseURL = config.baseURL;
        this.timeout = config.timeout || 60000; // 60 seconds for payment operations
        this.rejectedTokenHandler = config.onRejectedToken ?? null;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            // Which SDK and version sent the call: the Payments API readiness check counts them per store.
            'X-Tagada-SDK': `${SDK_NAME}/${SDK_VERSION}`,
            ...config.headers,
        };

        // Cleanup interval for circuit breaker history
        if (typeof setInterval !== 'undefined') {
            setInterval(() => this.cleanupHistory(), 10000);
        }
    }

    // Set a provider that returns a promise resolving to the token
    // This allows requests to wait until the token is ready
    setTokenProvider(provider: TokenProvider) {
        this.tokenProvider = provider;
    }

    // Convenience methods
    async get<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
        return this.request<T>('GET', url, undefined, config);
    }

    async post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>('POST', url, data, config);
    }

    async put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>('PUT', url, data, config);
    }

    async patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>('PATCH', url, data, config);
    }

    async delete<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
        return this.request<T>('DELETE', url, undefined, config);
    }

    // Update headers (useful for auth tokens)
    setHeader(key: string, value: string) {
        this.defaultHeaders[key] = value;
    }

    removeHeader(key: string) {
        delete this.defaultHeaders[key];
    }

    // Token management methods (matching old ApiService pattern)
    updateToken(token: string | null) {
        this.currentToken = token;
        if (token) {
            this.setHeader('x-cms-token', token);
            // console.log('[SDK] Token updated in ApiClient:', token.substring(0, 8) + '...');
        } else {
            this.removeHeader('x-cms-token');
            console.log('[SDK] Token removed from ApiClient');
        }
    }

    getCurrentToken(): string | null {
        return this.currentToken;
    }

    // Update configuration (useful for environment changes)
    updateConfig(config: Partial<ApiClientConfig>) {
        if (config.baseURL) {
            this.baseURL = config.baseURL;
        }
        if (config.timeout) {
            this.timeout = config.timeout;
        }
        if (config.headers) {
            Object.assign(this.defaultHeaders, config.headers);
        }
        console.log('[SDK] ApiClient configuration updated');
    }

    // ---- Core request method ----

    private async request<T>(
        method: string,
        url: string,
        data?: unknown,
        config?: RequestConfig,
        isRetry = false,
    ): Promise<T> {
        const requestKey = `${method}:${url}`;

        // Circuit Breaker Check
        try {
            this.checkRequestLimit(requestKey);
        } catch (error) {
            console.error('[SDK] 🛑 Request blocked by Circuit Breaker:', error);
            throw error;
        }

        // Token injection
        if (!config?.skipAuth && !this.currentToken && this.tokenProvider) {
            try {
                console.log('[SDK] Waiting for token...');
                const token = await this.tokenProvider();
                if (token) {
                    this.updateToken(token);
                }
            } catch (error) {
                console.error('[SDK] Failed to get token from provider:', error);
            }
        }

        // Build headers
        const headers: Record<string, string> = { ...this.defaultHeaders };
        if (config?.headers) {
            Object.assign(headers, config.headers);
        }
        const sentToken = config?.skipAuth ? null : this.currentToken;
        if (sentToken) {
            headers['x-cms-token'] = sentToken;
        }

        // Build URL
        let fullUrl = `${this.baseURL}${url}`;
        if (config?.params) {
            const searchParams = new URLSearchParams(config.params);
            fullUrl += `?${searchParams.toString()}`;
        }

        console.log(`[SDK] Making ${method} request to: ${fullUrl}`);

        // Timeout via AbortController
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        // Merge signals if caller provided one
        const signal = config?.signal
            ? anySignal([config.signal, controller.signal])
            : controller.signal;

        try {
            const fetchOptions: globalThis.RequestInit = {
                method,
                headers,
                signal,
            };

            if (data !== undefined) {
                fetchOptions.body = JSON.stringify(data);
            }

            const response = await fetch(fullUrl, fetchOptions);

            if (!response.ok) {
                const errorData = await this.safeParseJson(response);
                const error = this.toTagadaError(response.status, errorData, response.statusText);
                const renew = this.rejectedTokenHandler;
                const canRenew = !isRetry && config?.renewRejectedToken !== false;
                if (canRenew && sentToken && renew && isRejectedSession(error)) {
                    const freshToken = await renew(sentToken);
                    if (freshToken && freshToken !== sentToken) {
                        return await this.request<T>(method, url, data, config, true);
                    }
                }
                throw error;
            }

            const responseData = await response.json();
            return responseData as T;
        } catch (error) {
            if (error instanceof TagadaError) {
                throw error;
            }

            // AbortError → timeout (from our controller) or caller abort
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw new TagadaApiError('Request timed out', 0, {
                    code: TagadaErrorCode.TIMEOUT,
                    retryable: true,
                });
            }

            // TypeError → network failure (DNS, CORS, offline, etc.)
            if (error instanceof TypeError) {
                throw new TagadaNetworkError(error.message);
            }

            throw error instanceof Error ? error : new Error(String(error));
        } finally {
            clearTimeout(timeoutId);
        }
    }

    // ---- Error mapping ----

    /** Convert a non-ok fetch response into the appropriate TagadaError subclass. */
    private toTagadaError(
        status: number,
        data: Record<string, unknown> | undefined,
        statusText: string,
    ): TagadaError {
        const serverMessage =
            (data?.message as string) ??
            (data?.error as string) ??
            statusText;

        // Auth failures
        if (status === 401 || status === 403) {
            return new TagadaAuthError(serverMessage, status);
        }

        // Rate-limited
        if (status === 429) {
            return new TagadaApiError(serverMessage, 429, {
                code: TagadaErrorCode.RATE_LIMITED,
                retryable: true,
            });
        }

        // Not found
        if (status === 404) {
            return new TagadaApiError(serverMessage, 404, {
                code: TagadaErrorCode.NOT_FOUND,
                retryable: false,
            });
        }

        // Generic API error
        return new TagadaApiError(serverMessage, status, {
            code: (data?.code as string) ?? TagadaErrorCode.API_ERROR,
            details: data,
            retryable: status >= 500,
        });
    }

    // ---- Helpers ----

    private async safeParseJson(response: Response): Promise<Record<string, unknown> | undefined> {
        try {
            return await response.json();
        } catch {
            return undefined;
        }
    }

    // Circuit Breaker Implementation
    private checkRequestLimit(key: string) {
        const now = Date.now();
        const history = this.requestHistory.get(key);

        if (!history) {
            this.requestHistory.set(key, { count: 1, firstRequestTime: now });
            return;
        }

        if (now - history.firstRequestTime > this.WINDOW_MS) {
            // Window expired, reset
            this.requestHistory.set(key, { count: 1, firstRequestTime: now });
            return;
        }

        history.count++;
        if (history.count > this.MAX_REQUESTS) {
            throw new TagadaCircuitBreakerError(
                `Circuit Breaker: Too many requests to ${key} (${history.count} in ${this.WINDOW_MS}ms)`
            );
        }
    }

    private cleanupHistory() {
        const now = Date.now();
        for (const [key, history] of this.requestHistory.entries()) {
            if (now - history.firstRequestTime > this.WINDOW_MS) {
                this.requestHistory.delete(key);
            }
        }
    }
}

/**
 * Combine multiple AbortSignals into one that aborts when any input signal aborts.
 */
function anySignal(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    for (const signal of signals) {
        if (signal.aborted) {
            controller.abort(signal.reason);
            return controller.signal;
        }
        signal.addEventListener('abort', () => controller.abort(signal.reason), {
            once: true,
            signal: controller.signal,
        });
    }
    return controller.signal;
}
